export { planLabel, visibleSeatFields } from 'utils/learningHubUi';

export const HUB_TAB_FREEMIUM = 'freemium';
export const HUB_TAB_LEARNING_HUB = 'learning_hub';
export const LEARNING_HUB_PAGE_SIZES = [12, 24, 48];
export const DEFAULT_LEARNING_HUB_PAGE_SIZE = 12;

export const normalizePageSize = (value) => {
  const parsed = parseInt(value, 10);
  return LEARNING_HUB_PAGE_SIZES.includes(parsed) ? parsed : DEFAULT_LEARNING_HUB_PAGE_SIZE;
};

export const normalizePageIndex = (value) => {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

export const emptyHubPagination = (perpage = DEFAULT_LEARNING_HUB_PAGE_SIZE) => ({
  pageno: 0,
  perpage: normalizePageSize(perpage),
  data_count: 0,
  total_pages: 0,
  has_next: false,
  has_previous: false,
});

export const resolveHubPagination = (
  pagination,
  { pageno = 0, perpage = DEFAULT_LEARNING_HUB_PAGE_SIZE, courses = [] } = {}
) => {
  const requestedPage = normalizePageIndex(pageno);
  const requestedSize = normalizePageSize(perpage);
  if (pagination && typeof pagination === 'object') {
    const pageSize = normalizePageSize(pagination.perpage ?? requestedSize);
    const pageIndex = normalizePageIndex(pagination.pageno ?? requestedPage);
    const parsedCount = Number(pagination.data_count);
    const dataCount = Number.isFinite(parsedCount) ? parsedCount : courses.length;
    const parsedPages = Number(pagination.total_pages);
    const totalPages = Number.isFinite(parsedPages)
      ? parsedPages
      : (dataCount > 0 ? Math.ceil(dataCount / pageSize) : 0);
    return {
      pageno: pageIndex,
      perpage: pageSize,
      data_count: dataCount,
      total_pages: totalPages,
      has_next: pagination.has_next != null ? !!pagination.has_next : pageIndex < totalPages - 1,
      has_previous: pagination.has_previous != null ? !!pagination.has_previous : pageIndex > 0,
    };
  }
  return {
    pageno: requestedPage,
    perpage: requestedSize,
    data_count: courses.length,
    total_pages: courses.length > 0 ? 1 : 0,
    has_next: false,
    has_previous: requestedPage > 0,
  };
};

export const HUB_LEVELS = ['Foundational', 'Associate / Intermediate', 'Advanced'];
export const HUB_AUDIENCES = ['Tech', 'Non-tech'];
export const HUB_DURATIONS = ['Up to 2 hrs', '2-5 hrs', '5-10 hrs', 'Over 10 hrs'];

const asOptionList = (values) => (
  Array.isArray(values)
    ? values.map((value) => (typeof value === 'string' ? value.trim() : '')).filter(Boolean)
    : []
);

const matchingOptions = (values, allowed) => {
  const matched = asOptionList(values).filter((value) => allowed.includes(value));
  return matched.length ? matched : allowed.slice();
};

export const catalogueFilterOptions = (filters = {}) => ({
  levels: matchingOptions(filters?.levels, HUB_LEVELS),
  audiences: matchingOptions(filters?.audiences, HUB_AUDIENCES),
  durations: matchingOptions(filters?.durations, HUB_DURATIONS),
  certificateIssuers: asOptionList(filters?.certificate_issuers),
});

export const mergeCertificateIssuers = (current = [], next = [], issuerSelected = false) => {
  if (issuerSelected && current.length && current.length >= next.length) {
    return current;
  }
  return next;
};

export const certificateIssuerNames = (course = {}) => {
  const raw = course.certificate_issuers;
  const values = Array.isArray(raw) ? raw : (raw ? [raw] : []);
  const seen = new Set();
  return values.map((value) => String(value).trim()).filter((value) => {
    if (!value || seen.has(value)) {
      return false;
    }
    seen.add(value);
    return true;
  });
};

const textField = (value) => (typeof value === 'string' ? value.trim() : '');

// The repeated rose keeps catalogue labels that previously hashed to a weak
// orange on the same chip color as the Learning Hub AI labels.
export const CARD_ACCENTS = ['#2f6fde', '#6d4bb5', '#0e8f86', '#c43b66', '#c43b66', '#1f7eb5'];

export const cardAccent = (label) => {
  const text = textField(label) || 'course';
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash * 31 + text.charCodeAt(index)) >>> 0;
  }
  return CARD_ACCENTS[hash % CARD_ACCENTS.length];
};

export const chipSurface = (accent) => `color-mix(in srgb, ${accent} 82%, #172033)`;

export const levelTone = (level) => {
  const value = textField(level).toLowerCase();
  if (value.includes('advanced')) {
    return 'advanced';
  }
  if (value.includes('intermediate') || value.includes('associate')) {
    return 'intermediate';
  }
  if (value.includes('foundational') || value.includes('beginner')) {
    return 'foundational';
  }
  return '';
};

export const courseCardFields = (course = {}) => ({
  category: textField(course.category),
  subcategory: textField(course.source_category),
  name: course.name || '',
  provider: course.provider || '',
  certificateIssuers: certificateIssuerNames(course),
  level: course.level_label || '',
  duration: course.duration || '',
  audience: course.audience_group || '',
  isNew: course.is_new === true,
  image: course.imageUrl || course.thumbnail_url || course.image_url || '',
});

export const SKILL_LEVEL_LABELS = {
  foundational: 'Beginner',
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
};

export const skillLevelLabel = (level) => {
  if (!level) {
    return '';
  }
  const key = String(level).toLowerCase();
  return SKILL_LEVEL_LABELS[key] || String(level).replace(/_/g, ' ');
};

export const parseHubTab = (value) =>
  value === HUB_TAB_LEARNING_HUB ? HUB_TAB_LEARNING_HUB : HUB_TAB_FREEMIUM;

export const learningHubSubtitle = (tab, hubGranted) => {
  if (tab === HUB_TAB_LEARNING_HUB) {
    if (hubGranted) {
      return 'Hub access approved. Enroll or Launch any SkillUp course in this catalogue.';
    }
    return 'Request Hub access for SkillUp courses. After your Customer Admin or Project Admin approves, you can Enroll on every course here.';
  }
  return 'Freemium allowlist courses. Enroll or Launch instantly — no request needed.';
};

export const hubGrantedFromResponse = (response) => (
  response?.hub_granted === true || response?.access?.hub_granted === true
);

const ctaValue = (course) => (
  typeof course?.access_cta === 'string' ? course.access_cta.trim().toLowerCase() : ''
);

export const resolveAccessCta = (course, { tab = HUB_TAB_FREEMIUM, hubGranted = false } = {}) => {
  const raw = ctaValue(course);
  const enrolled = course?.enrolled === true || raw === 'launch';

  if (tab !== HUB_TAB_LEARNING_HUB) {
    return enrolled ? 'launch' : 'enroll';
  }

  const granted = hubGranted === true || course?.hub_granted === true;
  if (!granted) {
    return 'request';
  }
  return enrolled ? 'launch' : 'enroll';
};

export const accessCtaLabel = (cta, impersonating) => {
  switch (cta) {
    case 'launch':
      return impersonating ? 'Open course link' : 'Launch';
    case 'enroll':
      return 'Enroll';
    case 'upgrade':
      return 'Upgrade package';
    case 'request':
      return 'Request';
    default:
      return 'Enroll';
  }
};

export const filterOptionValue = (option) => {
  if (option == null) {
    return '';
  }
  if (typeof option === 'string' || typeof option === 'number') {
    return String(option);
  }
  return String(option.value ?? option.code ?? option.id ?? option.name ?? option.label ?? option.category ?? '');
};

export const filterOptionLabel = (option) => {
  if (option == null) {
    return '';
  }
  if (typeof option === 'string' || typeof option === 'number') {
    return skillLevelLabel(option) || String(option);
  }
  const raw = option.label ?? option.name ?? option.category ?? option.value ?? option.code ?? '';
  return skillLevelLabel(raw) || String(raw);
};

const subcategoryName = (sub) =>
  sub?.category || sub?.source_category || sub?.name || filterOptionValue(sub);

const countCourses = (courses = [], subcategories = []) => {
  const own = Array.isArray(courses) ? courses.length : 0;
  const nested = (Array.isArray(subcategories) ? subcategories : []).reduce(
    (sum, sub) => sum + (Array.isArray(sub.courses) ? sub.courses.length : (sub.count || 0)),
    0
  );
  return own + nested;
};

const normalizeSubcategory = (sub) => {
  const category = subcategoryName(sub);
  if (!category) {
    return null;
  }
  const courses = Array.isArray(sub.courses) ? sub.courses : [];
  return {
    category,
    source_category: category,
    courses,
    count: sub.count != null ? sub.count : courses.length,
  };
};

export const normalizeCategories = (data = [], filterTree = []) => {
  const fromData = (Array.isArray(data) ? data : []).map((item) => {
    const category = item.category || item.name || '';
    const courses = Array.isArray(item.courses) ? item.courses : [];
    let subcategories = Array.isArray(item.subcategories)
      ? item.subcategories.map(normalizeSubcategory).filter(Boolean)
      : [];

    if (!subcategories.length) {
      const grouped = courses.reduce((map, course) => {
        const source = course.source_category;
        if (!source) {
          return map;
        }
        if (!map.has(source)) {
          map.set(source, []);
        }
        map.get(source).push(course);
        return map;
      }, new Map());
      subcategories = Array.from(grouped.entries()).map(([name, subCourses]) => ({
        category: name,
        source_category: name,
        courses: subCourses,
        count: subCourses.length,
      }));
    }

    return {
      category,
      courses,
      subcategories,
      count: item.count != null ? item.count : countCourses(courses, subcategories),
    };
  }).filter((item) => item.category);

  if (fromData.length) {
    return fromData;
  }

  return (Array.isArray(filterTree) ? filterTree : []).map((item) => {
    if (typeof item === 'string') {
      return { category: item, courses: [], subcategories: [], count: 0 };
    }
    const category = item.category || item.name || item.label || filterOptionValue(item);
    const subcategories = Array.isArray(item.subcategories)
      ? item.subcategories.map(normalizeSubcategory).filter(Boolean)
      : [];
    return {
      category,
      courses: Array.isArray(item.courses) ? item.courses : [],
      subcategories,
      count: item.count != null ? item.count : countCourses(item.courses, subcategories),
    };
  }).filter((item) => item.category);
};

export const flattenVisibleCourses = (categories, activeCategory, activeSubCategory) => {
  const list = Array.isArray(categories) ? categories : [];
  if (!activeCategory) {
    const seen = new Set();
    const courses = [];
    list.forEach((item) => {
      const nested = (item.subcategories || []).flatMap((sub) => sub.courses || []);
      const own = item.courses || [];
      [...own, ...nested].forEach((course) => {
        const key = course?.uuid || course?.name;
        if (!key || seen.has(key)) {
          return;
        }
        seen.add(key);
        courses.push(course);
      });
    });
    return courses;
  }

  const parent = list.find((item) => item.category === activeCategory);
  if (!parent) {
    return list.flatMap((item) => item.courses || []);
  }

  if (activeSubCategory) {
    const sub = (parent.subcategories || []).find(
      (item) => item.category === activeSubCategory || item.source_category === activeSubCategory
    );
    if (sub) {
      return Array.isArray(sub.courses) ? sub.courses : [];
    }
    return (parent.courses || []).filter((course) => course.source_category === activeSubCategory);
  }

  if (parent.courses?.length) {
    return parent.courses;
  }
  return (parent.subcategories || []).flatMap((sub) => sub.courses || []);
};

const categoryTreeFrom = (response) => {
  if (Array.isArray(response?.filters?.category_tree) && response.filters.category_tree.length) {
    return response.filters.category_tree;
  }
  return Array.isArray(response?.data) ? response.data : [];
};

export const resolveTabPayload = (response, tab) => {
  const key = parseHubTab(tab);
  const tabMeta = response?.tabs?.[key];
  const label = tabMeta?.label || (key === HUB_TAB_LEARNING_HUB ? 'Learning Hub' : 'Freemium');
  return {
    key,
    label,
    count: Number.isFinite(Number(tabMeta?.count)) ? Number(tabMeta.count) : null,
    data: categoryTreeFrom(response),
    courses: Array.isArray(response?.courses) ? response.courses : null,
    filters: response?.filters || {},
    totals: response?.totals || null,
    pagination: response?.pagination || null,
  };
};

export const resolvePagedCourses = (tabPayload) => {
  if (Array.isArray(tabPayload?.courses)) {
    return tabPayload.courses;
  }
  return flattenVisibleCourses(normalizeCategories(tabPayload?.data || []), '', '');
};

export const buildLearningHubQuery = ({
  tab = HUB_TAB_FREEMIUM,
  search,
  category,
  level,
  duration,
  audience,
  issuer,
  sort,
  pageno = 0,
  perpage = DEFAULT_LEARNING_HUB_PAGE_SIZE,
}) => {
  const params = {
    tab: parseHubTab(tab),
    pageno: normalizePageIndex(pageno),
    perpage: normalizePageSize(perpage),
  };
  if (search) {
    params.search = search;
  }
  if (category) {
    params.category = category;
  }
  if (level) {
    params.level = level;
  }
  if (duration) {
    params.duration = duration;
  }
  if (audience) {
    params.audience = audience;
  }
  if (issuer) {
    params.issuer = issuer;
  }
  if (sort === 'az' || sort === 'za') {
    params.sort = sort;
  }
  return params;
};

export const resultLineForTab = (tab, showingCount, totals, hubGranted, pagination) => {
  const total = pagination?.data_count != null ? pagination.data_count : totals?.courses;
  if (total == null) {
    return null;
  }
  const pageIndex = pagination?.pageno ?? 0;
  const pageSize = pagination?.perpage ?? showingCount;
  const start = total === 0 ? 0 : pageIndex * pageSize + 1;
  const end = total === 0 ? 0 : Math.min(total, pageIndex * pageSize + (showingCount || 0));
  const range = total === 0 || start === end ? String(end) : `${start}–${end}`;
  if (tab === HUB_TAB_FREEMIUM) {
    return `Showing ${range} of ${total} Freemium courses.`;
  }
  if (hubGranted) {
    return `Showing ${range} of ${total} Learning Hub courses.`;
  }
  return `Showing ${range} of ${total} Learning Hub courses. Request access to enroll.`;
};

export const UPGRADE_PACKAGE_COPY = {
  title: 'Upgrade your Learning Hub package',
  body:
    'Freemium includes a curated allowlist of SkillUp courses. To unlock Enroll on the full SkillUp catalogue, ask your Customer Admin or Project Admin to upgrade this project to Learning Hub. This is a package change — it is not a Course Catalogue request.',
};
